import type { GameState } from './types.ts';

/**
 * The active-time clock. Time only accrues between `resume` (or the first letter) and
 * `pause`/completion. `now` is always passed in (epoch ms); a `now` earlier than the
 * last resume never makes time go backwards.
 */

export function elapsedMs(state: GameState, now: number): number {
	if (state.runningSince === null) return state.elapsedMs;
	return state.elapsedMs + Math.max(0, now - state.runningSince);
}

/** Stops the clock, folding the running span into `elapsedMs`. No-op when paused. */
export function pause(state: GameState, now: number): GameState {
	if (state.runningSince === null) return state;
	return { ...state, elapsedMs: elapsedMs(state, now), runningSince: null };
}

/** Restarts the clock. No-op before the first letter, after completion, or if running. */
export function resume(state: GameState, now: number): GameState {
	if (state.runningSince !== null || state.startedAt === null || state.completedAt !== null) return state;
	return { ...state, runningSince: now };
}

/**
 * Called by actions that count as play: starts the clock on the first one (when
 * `start` is true) and implicitly resumes a paused clock — if the player is acting, the
 * page is visible.
 */
export function touchClock(state: GameState, now: number, start: boolean): GameState {
	if (state.completedAt !== null) return state;
	if (state.startedAt === null) {
		if (!start) return state;
		return { ...state, startedAt: now, runningSince: now };
	}
	return resume(state, now);
}

/** Stops the clock for good. */
export function stopClock(state: GameState, now: number): GameState {
	const paused = pause(state, now);
	return { ...paused, completedAt: now };
}
